<template>
  <div v-if="visible" class="cookie-consent-overlay" data-nosnippet>
    <div class="cookie-consent-bar">
      <!-- Compact Bar View -->
      <div v-if="!showSettings" class="cookie-bar-compact">
        <div class="cookie-bar-text">
          <p>
            Bu web sitesi, size daha iyi bir deneyim sunmak, site trafiğini analiz etmek ve kişiselleştirilmiş
            içerik ve reklamlar göstermek için çerezleri kullanmaktadır.
            <a href="/pages/gizlilik" target="_blank" class="cookie-link">Gizlilik Politikası</a>
          </p>
        </div>
        <div class="cookie-bar-actions">
          <button class="cookie-btn cookie-btn-settings" @click="showSettings = true">Çerez Ayarları</button>
          <button class="cookie-btn cookie-btn-accept" @click="acceptAll">Tümünü Kabul Et</button>
        </div>
      </div>

      <!-- Detailed Settings View -->
      <div v-if="showSettings" class="cookie-settings">
        <div class="cookie-settings-header">
          <h3>Çerez Tercihleri</h3>
          <p class="cookie-settings-desc">
            Bu web sitesinde kullanılan çerezleri aşağıdan yönetebilirsiniz.
            Detaylı bilgi için <a href="/pages/gizlilik" target="_blank" class="cookie-link">Gizlilik Politikamızı</a> inceleyebilirsiniz.
          </p>
        </div>

        <div class="cookie-categories">
          <!-- Zorunlu Çerezler -->
          <div class="cookie-category">
            <div class="cookie-category-header" @click="toggleCategory('required')">
              <div class="cookie-category-left">
                <span :class="['cookie-accordion-icon', { 'is-open': openCategories.required }]">
                  <img onload="SVGInject(this)" src="https://gcdn.bionluk.com/site/cicons/ic-mini-arrow-down.svg" />
                </span>
                <span class="cookie-category-title">Zorunlu Çerezler</span>
              </div>
              <span class="cookie-always-active">Her Zaman Etkin</span>
            </div>
            <div v-show="openCategories.required" class="cookie-category-body">
              <p>
                Zorunlu çerezler, Platform'u görüntülemeniz esnasında cihazınıza yerleştirilen ve sunulan
                online servislerin düzgün şekilde çalışabilmesi için gerekli olan çerezlerdir. Söz konusu
                çerezler kullanıcının talep etmiş olduğu bir bilgi toplumu hizmetinin yerine getirilmesi için
                zorunlu olarak kullanılmaktadır.
              </p>
            </div>
          </div>

          <!-- Performans ve Analiz Çerezleri -->
          <div class="cookie-category">
            <div class="cookie-category-header" @click="toggleCategory('analytics')">
              <div class="cookie-category-left">
                <span :class="['cookie-accordion-icon', { 'is-open': openCategories.analytics }]">
                  <img onload="SVGInject(this)" src="https://gcdn.bionluk.com/site/cicons/ic-mini-arrow-down.svg" />
                </span>
                <span class="cookie-category-title">Performans ve Analiz İçin Gerekli Olan Çerezler</span>
              </div>
              <custom-switch v-model="preferences.analytics" @click.native.stop />
            </div>
            <div v-show="openCategories.analytics" class="cookie-category-body">
              <p>
                Performans çerezleri, Platform ziyaret ve trafiğini takip ve analiz etmemizi ve bu analiz
                amacıyla istatistiki ölçüm yapmamızı sağlar. Bu çerezler sayesinde Platform üzerindeki
                alanlardan hangilerinin en sık ya da seyrek ziyaret edildiği gibi bilgileri edinebilir ve
                Platform'un trafiğini optimize edebiliriz. İlgili bilgi toplumu hizmetini açıkça talep etmediğiniz
                hallerde söz konusu olan çerezlerin ve üçüncü taraf analitik çerezlerinin kullanımı esnasında
                gerçekleştirdiğimiz veri işleme faaliyetleri için Kanun m.5 kapsamında, söz konusu olması
                halinde, açık rızanıza dayanmaktayız.
              </p>
            </div>
          </div>

          <!-- Hedefleme ve Kişiselleştirilmiş Reklam Çerezleri -->
          <div class="cookie-category">
            <div class="cookie-category-header" @click="toggleCategory('marketing')">
              <div class="cookie-category-left">
                <span :class="['cookie-accordion-icon', { 'is-open': openCategories.marketing }]">
                  <img onload="SVGInject(this)" src="https://gcdn.bionluk.com/site/cicons/ic-mini-arrow-down.svg" />
                </span>
                <span class="cookie-category-title">Hedefleme ve Kişiselleştirilmiş Reklam Çerezleri</span>
              </div>
              <custom-switch v-model="preferences.marketing" @click.native.stop />
            </div>
            <div v-show="openCategories.marketing" class="cookie-category-body">
              <p>
                Hedefleme ve kişiselleştirilmiş reklam çerezleri, sizlere Platform'da veya Platform haricindeki
                mecralarda görüntüleme geçmişinize ve ziyaretçi profilinize uygun olarak kişiselleştirilmiş
                ürün ve hizmet tanıtımı yapmak için kullanılır. Bu çerezlerin kullanımı esnasında
                gerçekleştirdiğimiz veri işleme faaliyetleri için Kanun m.5 kapsamında veri işleme şartı olarak
                "açık rızanıza" dayanmaktayız.
              </p>
            </div>
          </div>
        </div>

        <div class="cookie-settings-actions">
          <button class="cookie-btn cookie-btn-save" @click="savePreferences">Tercihlerimi Kaydet</button>
          <button class="cookie-btn cookie-btn-accept" @click="acceptAll">Tümünü Kabul Et</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { saveConsent, hasConsent, getConsent, loadAnalyticsScripts, loadMarketingScripts } from '@/utils/cookieConsent';
import CustomSwitch from '@/pages/components/CustomSwitch';
import EventBus from '@/eventBus';

export default {
  name: 'CookieConsent',
  components: { CustomSwitch },
  data() {
    return {
      visible: false,
      showSettings: false,
      preferences: {
        analytics: false,
        marketing: false
      },
      openCategories: {
        required: false,
        analytics: false,
        marketing: false
      }
    };
  },
  methods: {
    toggleCategory(category) {
      this.openCategories[category] = !this.openCategories[category];
    },

    trackCurrentAnalyticsPageView() {
      if (this.Helper.isIndexerBotOrDevelopmentEnv()) {
        return;
      }

      const fullPath = this.$store.state.route && this.$store.state.route.fullPath
        ? this.$store.state.route.fullPath
        : window.location.pathname + window.location.search;

      window.ga('send', {
        hitType: 'pageview',
        title: document.title,
        location: window.location.origin + fullPath,
        page: fullPath
      });
    },

    acceptAll() {
      this.preferences.analytics = true;
      this.preferences.marketing = true;
      this.applyAndClose();
    },

    savePreferences() {
      this.applyAndClose();
    },

    applyAndClose() {
      const previousConsent = getConsent() || {};
      const analyticsJustGranted = !previousConsent.analytics && this.preferences.analytics;

      saveConsent(this.preferences);

      if (this.preferences.analytics) {
        loadAnalyticsScripts();
      }
      if (this.preferences.marketing) {
        loadMarketingScripts();
      }

      if (analyticsJustGranted) {
        if (this.$store.state.user && this.$store.state.user.uuid) {
          this.Helper.trackEvents.setUser();
        }
        this.trackCurrentAnalyticsPageView();
      }

      this.visible = false;
      this.showSettings = false;
    },

    openPanel() {
      const consent = getConsent();
      if (consent) {
        this.preferences.analytics = consent.analytics;
        this.preferences.marketing = consent.marketing;
      }
      this.showSettings = true;
      this.visible = true;
    }
  },

  created() {
    EventBus.$on('open-cookie-consent', this.openPanel);
  },

  mounted() {
    if (!hasConsent()) {
      this.visible = true;
    }
  },

  beforeDestroy() {
    EventBus.$off('open-cookie-consent', this.openPanel);
  }
};
</script>

<style scoped lang="scss">
.cookie-consent-overlay {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 99999;
  display: flex;
  justify-content: center;
  pointer-events: none;
}

.cookie-consent-bar {
  width: 100%;
  max-width: 1200px;
  background-color: #ffffff;
  border-top: 1px solid #e0e0e0;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.1);
  pointer-events: all;
  border-radius: 12px 12px 0 0;
  overflow: hidden;
}

/* Compact Bar */
.cookie-bar-compact {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 30px;
  gap: 20px;
}

.cookie-bar-text {
  flex: 1;

  p {
    font-size: 14px;
    line-height: 1.6;
    color: #2d3640;
    margin: 0;
  }
}

.cookie-bar-actions {
  display: flex;
  gap: 12px;
  flex-shrink: 0;
}

.cookie-link {
  color: #fd4056;
  text-decoration: underline;
  font-weight: 500;

  &:hover {
    color: #e0354a;
  }
}

/* Buttons */
.cookie-btn {
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
  white-space: nowrap;
  font-family: 'sofia-pro', sans-serif;
}

.cookie-btn-settings {
  background-color: #ffffff;
  color: #2d3640;
  border: 1px solid #bfc8d2;

  &:hover {
    background-color: #f5f5f5;
  }
}

.cookie-btn-accept {
  background-color: #fd4056;
  color: #ffffff;

  &:hover {
    background-color: #e0354a;
  }
}

.cookie-btn-save {
  background-color: #ffffff;
  color: #2d3640;
  border: 1px solid #bfc8d2;

  &:hover {
    background-color: #f5f5f5;
  }
}

/* Settings Panel */
.cookie-settings {
  padding: 24px 30px 20px;
  max-height: 70vh;
  overflow-y: auto;
}

.cookie-settings-header {
  margin-bottom: 20px;

  h3 {
    font-size: 20px;
    font-weight: 700;
    color: #2d3640;
    margin: 0 0 8px 0;
  }
}

.cookie-settings-desc {
  font-size: 14px;
  line-height: 1.6;
  color: #5e6b79;
  margin: 0;
}

/* Categories */
.cookie-categories {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.cookie-category {
  border: 1px solid #e8ecf0;
  border-radius: 8px;
  margin-bottom: 12px;
  overflow: hidden;
}

.cookie-category-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  cursor: pointer;
  background-color: #fafbfc;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f2f4f6;
  }
}

.cookie-category-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.cookie-accordion-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  transition: transform 0.2s ease;

  img, svg {
    width: 14px;
    height: 14px;
  }

  &.is-open {
    transform: rotate(180deg);
  }
}

.cookie-category-title {
  font-size: 15px;
  font-weight: 600;
  color: #2d3640;
}

.cookie-always-active {
  font-size: 13px;
  font-weight: 600;
  color: #fd4056;
}

.cookie-category-body {
  padding: 0 20px 16px 50px;
  background-color: #ffffff;

  p {
    font-size: 13px;
    line-height: 1.7;
    color: #5e6b79;
    margin: 0;
  }
}

/* Settings Actions */
.cookie-settings-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e8ecf0;
}

/* Mobile */
@media (max-width: 768px) {
  .cookie-bar-compact {
    flex-direction: column;
    padding: 16px 20px;
    gap: 16px;
  }

  .cookie-bar-actions {
    width: 100%;
    justify-content: stretch;

    .cookie-btn {
      flex: 1;
    }
  }

  .cookie-settings {
    padding: 16px 20px;
  }

  .cookie-category-header {
    padding: 14px 16px;
  }

  .cookie-category-body {
    padding: 0 16px 14px 40px;
  }

  .cookie-settings-actions {
    flex-direction: column-reverse;

    .cookie-btn {
      width: 100%;
    }
  }

  .cookie-category-title {
    font-size: 13px;
  }
}
</style>
